import { EditorProvider } from '@/components/ui/kibo-ui/editor';
import { Button } from '@/components/ui/button';
import type { Editor, EditorEvents } from '@tiptap/core';
import type { JSONContent } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/providers/i18n';
import { TextEditDialog } from './edit-dialog';
import { cn, textToTiptapJSON } from '@/lib/utils';
 

type TextEditDialogEditorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  initialContent?: JSONContent | null;
  initialText?: string;
  onSave: (result: { text: string; json: JSONContent }) => void;
  onChange?: (result: { text: string; json: JSONContent }) => void;
  onCancel?: () => void;
  placeholder?: string;
  readOnly?: boolean;
};

export function TextEditDialogEditor({
  open,
  onOpenChange,
  title = 'Edit Text',
  initialContent,
  initialText,
  onSave,
  onChange,
  onCancel,
  placeholder = 'Start typing...',
  readOnly,
}: TextEditDialogEditorProps) {
  const { t } = useI18n();
  const dialogEditor = useRef<Editor | null>(null);
  const [content, setContent] = useState<JSONContent | null>(null);
  const isReadOnly = Boolean(readOnly);

  useEffect(() => {
    if (!open) return;
    if (initialContent) {
      setContent(initialContent);
    } else if (typeof initialText === 'string') {
      setContent(textToTiptapJSON(initialText));
    } else {
      setContent(null);
    }
  }, [open, initialContent, initialText]);

  const handleDialogCreate = (props: EditorEvents['create']) => {
    dialogEditor.current = props.editor;
    try {
      props.editor?.setEditable?.(!isReadOnly);
    } catch {}
    // Avoid focusing in read-only mode (e.g., prevents mobile keyboard popups)
    if (isReadOnly) return;
    try {
      props.editor.commands.focus('end');
    } catch {
      props.editor.chain().focus().run();
    }
  };

  const handleDialogUpdate = ({ editor }: { editor: Editor }) => {
    const json = editor.getJSON();
    setContent(json);
    if (!isReadOnly && onChange) {
      const text = editor.getText();
      onChange({ text, json });
    }
  };

  const handleHardBreakKeyDown = (e: React.KeyboardEvent) => {
    if (isReadOnly) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      try {
        dialogEditor.current?.chain().focus().setHardBreak().run();
      } catch {}
    }
  };

  const handleSave = () => {
    if (isReadOnly) return;
    const text = dialogEditor.current?.getText() ?? '';
    const json = (content ?? dialogEditor.current?.getJSON()) as JSONContent;
    onSave({ text, json });
  };

  return (
    <TextEditDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      onSave={handleSave}
      onCancel={onCancel}
      footer={
        isReadOnly ? (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="rounded-xl h-9 px-4 text-xs font-bold hover:bg-sidebar-accent"
              onClick={() => {
                try { onCancel?.(); } catch {}
                try { onOpenChange(false); } catch {}
              }}
            >
              {t('Cancel')}
            </Button>
            <Button size="sm" className="rounded-xl h-9 px-6 text-xs font-bold bg-muted text-muted-foreground cursor-not-allowed" disabled>
              {t('Save')}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div onKeyDownCapture={handleHardBreakKeyDown}>
        <EditorProvider
          onCreate={handleDialogCreate}
          immediatelyRender={false}
          content={content}
          placeholder={placeholder ?? t('Start typing...')}
          editable={!isReadOnly}
          className={cn(
            'prose prose-sm dark:prose-invert size-full p-6',
            '[&_p:first-child]:mt-0',
            '[&_p:last-child]:mb-0'
          )}
          onUpdate={handleDialogUpdate}
        />
      </div>
    </TextEditDialog>
  );
}

// centralized in lib/utils.ts


